import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import validate from 'utils/validate';
import { acceptDigits } from 'utils/helpers';
import {
  PrimaryButton,
  Text,
  FormattedError,
  Heading,
  Box,
  Link,
  Panel,
  PanelContent,
} from 'components';
import { I18nContext } from 'components/I18n';
import {
  FormField,
  Checkbox,
  TextField,
} from 'components/Forms';

function CreateAccountFormComponent(props) {
  const {
    error,
    brandNamePossessive,
    termsAndConditionsUrl,
    showTermsCheckbox,
    onSubmit,
  } = props;
  const i18n = useContext(I18nContext);
  const methods = useForm({ mode: 'onChange' });
  const { handleSubmit } = methods;
  
  const { loginEnabled, location } = useSelector(state => ({
    loginEnabled: state.settings.loginEnabled,
    location: state.router.location,
  }));

  return (
    <Panel textAlign="center" maxWidth="34rem">
      <Heading id="createAccount" mb="medium" />

      <Heading
        id="createAccount.subHeader"
        fontSize="medium"
        mb="medium"
        as="h2"
      />
      <FormProvider {...methods}>
        <PanelContent centered as="form" onSubmit={handleSubmit(onSubmit)} method="POST" noValidate>
          <FormField
            as={TextField}
            name="customerId"
            placeholder={i18n.formatText('createAccount.customerId')}
            type="text"
            normalize={acceptDigits}
            validations={{
              required: validate.required('validate.customerIdRequired'),
            }}
          />
          <FormField
            as={TextField}
            name="password"
            placeholder={i18n.formatText('password')}
            type="password"
            validations={{
              required: validate.required('validate.passRequired'),
            }}
          />
          {termsAndConditionsUrl && showTermsCheckbox && (
            <FormField
              as={Checkbox}
              name="termsAndConditions"
              label={{
                id: 'createAccount.termsAndConditions',
                values: {
                  termsAndConditionsLink: (
                    <Link
                      href={termsAndConditionsUrl}
                      target="_blank"
                      id="createAccount.termsAndConditionsLink"
                    />
                  ),
                },
              }}
              validations={{
                required: validate.required('validate.termsAndConditionsRequired'),
              }}
            />
          )}
          <FormattedError error={error} />

          <Box my="medium">
            <PrimaryButton variant="brand" type="submit">
              <Text id="createAccount" />
            </PrimaryButton>
          </Box>

          {termsAndConditionsUrl && !showTermsCheckbox && (
            <Box m="fine">
              <Text
                id="createAccount.termsMessage"
                values={{
                  brandNamePossessive,
                  termsAndConditionsLink: (
                    <Link
                      key="termsAndConditionsUrl"
                      href={termsAndConditionsUrl}
                      target="_blank"
                      id="termsAndConditions"
                    />
                  ),
                }}
                fontSize="fine"
              />
            </Box>
          )}
          
          {loginEnabled && (
            <Box mt="medium">
              <Text
                bold
                color="secondary"
                fontSize="medium"
                id="haveAccountLogIn"
                values={{
                  logInLink: (
                    <Link
                      key="logInLink"
                      to={{
                        name: 'log-in',
                        params: {
                          platform: location.params?.platform,
                        },
                      }}
                      id="logIn"
                    />
                  ),
                }}
              />
            </Box>
          )}
        </PanelContent>
      </FormProvider>
    </Panel>
  );
}

CreateAccountFormComponent.propTypes = {
  brandNamePossessive: PropTypes.string.isRequired,
  showTermsCheckbox: PropTypes.bool,
  termsAndConditionsUrl: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default CreateAccountFormComponent;
